import { sendSMSWithTestCredentials } from './twilio';
import { supabase } from './supabase';
import { recordOpportunityActivity } from './opportunityActivities';

interface SendSMSParams {
  businessId: string;
  to: string;
  message: string;
  aiGenerated?: boolean;  // Add AI flag parameter
  mediaFiles?: File[];  // Add media files parameter
  customerId?: string;  // Optional: if customer ID is known, pass it to avoid lookup
}

interface SendSMSResult {
  success: boolean;
  error?: string;
  message?: any;
}

/**
 * Sends an SMS message using Twilio
 */
export async function sendSMS({ businessId, to, message, aiGenerated = false, mediaFiles, customerId }: SendSMSParams): Promise<SendSMSResult> {
  try {
    console.log(`[SMS] 🚀 Sending SMS: to=${to}, messageLength=${message.length}, businessId=${businessId}, aiGenerated=${aiGenerated}, mediaCount=${mediaFiles?.length || 0}`);
    
    // Use the existing Twilio functions that we know work (from the settings page)
    const result = await sendSMSWithTestCredentials(businessId, to, message, aiGenerated, mediaFiles, customerId);
    
    if (result.success) {
      console.log(`[SMS] ✅ SMS sent successfully`);
      
      // Record activity for CRM opportunities (non-blocking)
      recordActivityForSMS(businessId, to, message, customerId).catch(err => {
        console.error('[SMS] Error recording opportunity activity:', err);
      });
    } else {
      console.error(`[SMS] ❌ Failed to send SMS:`, result.error);
    }
    
    return result;
  } catch (error) {
    console.error('[SMS] ❌ Error in sendSMS:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to send SMS'
    };
  }
}

/**
 * Helper function to record SMS activity for CRM opportunities
 */
async function recordActivityForSMS(businessId: string, phoneNumber: string, message: string, knownCustomerId?: string): Promise<void> {
  try {
    if (!supabase) return;

    let customerId = knownCustomerId;

    // If customer ID not provided, look it up by phone number
    if (!customerId) {
      // Normalize phone number for lookup (remove non-digits)
      const normalizedPhone = phoneNumber.replace(/\D/g, '');
      
      // Get all customers with phone numbers for this business
      const { data: matchingCustomers, error } = await supabase
        .from('customers')
        .select('id, phone')
        .eq('business_id', businessId)
        .not('phone', 'is', null);

      if (error || !matchingCustomers || matchingCustomers.length === 0) {
        // No customers found - nothing to record
        return;
      }

      // Find customer whose phone number matches (normalized)
      const match = matchingCustomers.find(c => {
        if (!c.phone) return false;
        const customerPhoneNormalized = c.phone.replace(/\D/g, '');
        return customerPhoneNormalized === normalizedPhone || 
               customerPhoneNormalized === normalizedPhone.substring(1) ||
               `1${customerPhoneNormalized}` === normalizedPhone;
      });

      if (match) {
        customerId = match.id;
      } else {
        // No matching customer found
        return;
      }
    }

    // Record activity
    await recordOpportunityActivity({
      businessId,
      customerId,
      activityType: 'sms_sent',
      activityData: {
        message_preview: message.substring(0, 100), // First 100 chars
        to: phoneNumber
      }
    });
  } catch (error) {
    console.error('[SMS Activity] Error recording activity:', error);
  }
}

/**
 * Sends a notification SMS to a customer
 */
export async function sendCustomerNotification(
  businessId: string,
  customerPhone: string,
  template: 'booking_confirmation' | 'booking_reminder' | 'booking_cancelled',
  data: Record<string, string>
): Promise<SendSMSResult> {
  const templates = {
    booking_confirmation: `Your booking {booking_number} has been confirmed for {date} at {time}. We look forward to seeing you!`,
    booking_reminder: `Reminder: Your booking {booking_number} is scheduled for tomorrow at {time}. See you then!`,
    booking_cancelled: `Your booking {booking_number} has been cancelled. If you have any questions, please contact us.`
  };

  let message = templates[template];
  
  // Replace placeholders with actual data
  Object.entries(data).forEach(([key, value]) => {
    message = message.replace(`{${key}}`, value);
  });

  return sendSMS({
    businessId,
    to: customerPhone,
    message
  });
}

/**
 * Sends a custom SMS message to a customer
 */
export async function sendCustomMessage(
  businessId: string,
  customerPhone: string,
  message: string
): Promise<SendSMSResult> {
  console.log(`[CUSTOM SMS] 🚀 Sending custom message to ${customerPhone}`);
  
  if (!businessId) {
    console.error('[CUSTOM SMS] ❌ Missing business ID');
    return { success: false, error: 'Missing business ID' };
  }
  
  if (!customerPhone) {
    console.error('[CUSTOM SMS] ❌ Missing customer phone number');
    return { success: false, error: 'Missing customer phone number' };
  }
  
  if (!message || message.trim() === '') {
    console.error('[CUSTOM SMS] ❌ Empty message');
    return { success: false, error: 'Empty message' };
  }
  
  const result = await sendSMS({
    businessId,
    to: customerPhone,
    message
  });
  
  if (result.success) {
    console.log(`[CUSTOM SMS] ✅ Message sent successfully to ${customerPhone}`);
  } else {
    console.error(`[CUSTOM SMS] ❌ Failed to send to ${customerPhone}:`, result.error);
  }
  
  return result;
} 